import React, { useCallback, useEffect, useState } from "react";
import { ReactComponent as DropdownDownIcon } from "./assets/dropdown-iterative-down.svg";
import { ReactComponent as DropdownUpIcon } from "./assets/dropdown-iterative-up.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as MinusIcon } from "./assets/minus.svg";
import {
  CounterButton,
  CounterContainer,
  CounterInputContainer,
  CounterInputContent,
  CounterInputField,
  CounterInputSection,
  CounterOverlay,
  CounterValue,
  CounterValueContainer,
  CyclesSection,
  DropdownItem,
  DropdownList,
  MainContainer,
  MinusIconContainer,
  PlusIconContainer,
  SecondaryLabel,
  SectionContent,
  SectionHeader,
  SectionTitle,
  VehicleDropdownIcon,
  VehicleInputContainer,
  VehicleInputContent,
  VehicleInputField,
  VehicleInputSection,
  VehicleOverlay,
  FleetCheckboxButton,
  FleetCheckboxVisual,
  VehiclePlaceholder,
  VehicleSection,
} from "./styles";
import {
  VEHICLE_DROPDOWN_SELECTOR,
  MIN_CYCLES,
  isClickOutsideElement,
  calculateNewCycles,
  getDisplayVehicleName,
} from "./utils";

interface MissionDeployProps {
  handleVehiclePick: (vehicleId: string, selectedLoopsValue: string) => void;
  handleFleetPick: (currentFleetId: string, selectedLoopsValue: string) => void;
  cancelAction: () => void;
  currentMissionDataStops: Array<string>;
  vehicleName: string;
  vehicleId: string | null;
  currentVehicles: Vehicle[];
  cycles?: number;
  onCyclesChange?: (cycles: number) => void;
  resetTrigger?: number;
  vehicleType?: string | null; // When set, only show vehicles matching this type
  fleetId?: string | null; // Fleet ID for "assign to first available" mode
}

export const MissionDeploy: React.FC<MissionDeployProps> = ({
  handleVehiclePick,
  handleFleetPick,
  currentMissionDataStops,
  vehicleName,
  currentVehicles,
  cycles = 1,
  onCyclesChange,
  resetTrigger = 0,
  vehicleType,
  fleetId,
}) => {
  // Filter vehicles by vehicle type when constraint is set
  const filteredVehicles = vehicleType
    ? currentVehicles.filter((vehicle) => vehicle.vehicleTypeId === vehicleType)
    : currentVehicles;
  const [localCycles, setLocalCycles] = useState(cycles);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [assignToFleet, setAssignToFleet] = useState(false);

  // Computed values
  const isOnlyOneStop = currentMissionDataStops.length === 1;
  const displayVehicleName = getDisplayVehicleName(
    selectedVehicle,
    vehicleName
  );

  // Effects
  useEffect(() => {
    setSelectedVehicle(null);
    setAssignToFleet(false);
  }, [resetTrigger]);

  // Clear selected vehicle when the type filter changes — prevents deploying a
  // previously-selected vehicle that no longer matches the new type constraint.
  useEffect(() => {
    setSelectedVehicle(null);
  }, [vehicleType]);

  useEffect(() => {
    if (isOnlyOneStop && localCycles !== MIN_CYCLES) {
      setLocalCycles(MIN_CYCLES);
      onCyclesChange?.(MIN_CYCLES);
      if (assignToFleet && fleetId) {
        handleFleetPick(fleetId, MIN_CYCLES.toString());
      } else if (selectedVehicle) {
        handleVehiclePick(selectedVehicle.id, MIN_CYCLES.toString());
      }
    }
  }, [
    isOnlyOneStop,
    localCycles,
    onCyclesChange,
    assignToFleet,
    fleetId,
    selectedVehicle,
    handleFleetPick,
    handleVehiclePick,
  ]);

  // Event handlers
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (isDropdownOpen) {
        const target = event.target as Element;
        if (isClickOutsideElement(target, VEHICLE_DROPDOWN_SELECTOR)) {
          setIsDropdownOpen(false);
        }
      }
    },
    [isDropdownOpen]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
    return undefined;
  }, [isDropdownOpen, handleClickOutside]);

  const updateCyclesAndNotifyParent = useCallback(
    (newCycles: number) => {
      setLocalCycles(newCycles);
      onCyclesChange?.(newCycles);
      if (assignToFleet && fleetId) {
        handleFleetPick(fleetId, newCycles.toString());
      } else if (selectedVehicle) {
        handleVehiclePick(selectedVehicle.id, newCycles.toString());
      }
    },
    [
      assignToFleet,
      fleetId,
      selectedVehicle,
      onCyclesChange,
      handleFleetPick,
      handleVehiclePick,
    ]
  );

  const handleDecreaseCycles = useCallback(() => {
    const newCycles = calculateNewCycles(localCycles, "decrease");
    updateCyclesAndNotifyParent(newCycles);
  }, [localCycles, updateCyclesAndNotifyParent]);

  const handleIncreaseCycles = useCallback(() => {
    const newCycles = calculateNewCycles(localCycles, "increase");
    updateCyclesAndNotifyParent(newCycles);
  }, [localCycles, updateCyclesAndNotifyParent]);

  const handleDropdownToggle = useCallback(() => {
    setIsDropdownOpen((prev) => !prev);
  }, []);

  const handleVehicleSelect = useCallback(
    (vehicle: Vehicle) => {
      setSelectedVehicle(vehicle);
      setIsDropdownOpen(false);
      handleVehiclePick(vehicle.id, localCycles.toString());
    },
    [handleVehiclePick, localCycles]
  );

  const handleAssignToFleetChange = useCallback(
    (checked: boolean) => {
      setAssignToFleet(checked);
      if (checked && fleetId) {
        setSelectedVehicle(null);
        setIsDropdownOpen(false);
        handleFleetPick(fleetId, localCycles.toString());
      } else if (!checked) {
        handleFleetPick("", localCycles.toString());
      }
    },
    [fleetId, localCycles, handleFleetPick]
  );

  return (
    <MainContainer>
      <CyclesSection>
        <SectionHeader>
          <SectionTitle>Number of Cycles</SectionTitle>
        </SectionHeader>
        <SectionContent>
          <CounterContainer>
            <CounterButton onClick={handleDecreaseCycles}>
              <MinusIconContainer>
                <MinusIcon />
              </MinusIconContainer>
            </CounterButton>
            <CounterInputContainer>
              <CounterInputSection>
                <CounterInputField>
                  <CounterOverlay />
                  <CounterInputContent>
                    <CounterValueContainer>
                      <CounterValue>{localCycles}</CounterValue>
                    </CounterValueContainer>
                  </CounterInputContent>
                </CounterInputField>
              </CounterInputSection>
            </CounterInputContainer>
            <CounterButton onClick={handleIncreaseCycles}>
              <PlusIconContainer>
                <AddIcon />
              </PlusIconContainer>
            </CounterButton>
          </CounterContainer>
          {fleetId && (
            <FleetCheckboxButton>
              <input
                type="checkbox"
                checked={assignToFleet}
                onChange={(e) => handleAssignToFleetChange(e.target.checked)}
              />
              <FleetCheckboxVisual $checked={assignToFleet} />
              <SectionTitle>Assign to first available</SectionTitle>
            </FleetCheckboxButton>
          )}
        </SectionContent>
      </CyclesSection>
      <VehicleSection>
        <SectionHeader>
          <SectionTitle>Assign to vehicle</SectionTitle>
        </SectionHeader>
        <VehicleInputContainer>
          <VehicleInputSection data-vehicle-dropdown>
            <VehicleInputField
              onClick={assignToFleet ? undefined : handleDropdownToggle}
              $disabled={assignToFleet}
            >
              <VehicleInputContent>
                <VehiclePlaceholder>
                  <SecondaryLabel>{displayVehicleName}</SecondaryLabel>
                </VehiclePlaceholder>
              </VehicleInputContent>
              <VehicleDropdownIcon>
                {isDropdownOpen ? <DropdownUpIcon /> : <DropdownDownIcon />}
              </VehicleDropdownIcon>
            </VehicleInputField>
            {assignToFleet && <VehicleOverlay />}
            {!assignToFleet && isDropdownOpen && (
              <DropdownList>
                {filteredVehicles.length > 0 ? (
                  filteredVehicles.map((vehicle, index) => (
                    <DropdownItem
                      key={vehicle.id || index}
                      onClick={() => handleVehicleSelect(vehicle)}
                    >
                      {vehicle.name}
                    </DropdownItem>
                  ))
                ) : (
                  <DropdownItem>
                    {vehicleType
                      ? "No vehicles of the required type available"
                      : "No vehicles available"}
                  </DropdownItem>
                )}
              </DropdownList>
            )}
          </VehicleInputSection>
        </VehicleInputContainer>
      </VehicleSection>
    </MainContainer>
  );
};
